<template>
  <div class="micro-hitl-widget">
    <div v-if="phase === 'auth'" class="micro-hitl-widget__auth">
      <p
        class="micro-hitl-widget__auth-hint"
        :class="{ 'micro-hitl-widget__auth-hint--error': authError }"
      >
        {{ authError ? authError : 'Completing authorization…' }}
      </p>
    </div>
    <!-- initialize() mounts the floating control (shadow / popup per bundle defaults) -->
    <div v-show="phase === 'embed'" class="micro-hitl-widget__root" />
  </div>
</template>

<script>
import authLoadingSrc from '@/assets/loading.gif';
import { COOKIE_DOMAIN, COOKIE_PREFIX, HITL_ORIGIN } from '@/constants/env.js';
import { exchangeJwtForOrCookie, isOrCookieTokenAllowed } from '@/utils/orCookieSession.js';

/**
 * Floating mini-HITL widget: launcher UMD from hosted core
 * (`/core/<version>/shepherd/shepherd-agent-ui-mini-launcher.min.js`).
 * Vue 2 is loaded from unpkg first so the UMD bundle can attach to `window`.
 */
const BUNDLE_MAIN = 'shepherd/shepherd-agent-ui-mini-launcher.min.js';
const VERSION = 'v1.8.1-rc.0';
/** Base origin only — launcher URL is `${HITL_ORIGIN}/core/${VERSION}/${BUNDLE_MAIN}` */
const VUE_CDN_URL = 'https://unpkg.com/vue@2.6.14';

const SCRIPT_ID = {
  vue: 'micro-hitl-vue2-cdn',
  /** Stable id for teardown (bundle filename is awkward in CSS selectors). */
  launcher: 'micro-hitl-agent-ui-micro-launcher-min',
};

/**
 * Append a real &lt;script src&gt; (same end result as a script tag in HTML).
 * Vue SFC templates cannot carry executable script tags — the compiler strips them.
 */
function appendScript(src, id, { attrs = [] } = {}) {
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(id);
    if (existing?.getAttribute('src') === src) {
      resolve();
      return;
    }
    if (existing) existing.remove();

    const el = document.createElement('script');
    el.id = id;
    el.src = src;
    el.async = true;
    for (const name of attrs) {
      el.setAttribute(name, '');
    }
    el.onload = () => resolve();
    el.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(el);
  });
}

export default {
  name: 'MicroHitlWidget',
  props: {
    /** Shepherd JWT sent to the auth exchange when the `or` cookie is missing or invalid. */
    jwtToken: {
      type: String,
      default: '',
    },
  },
  emits: ['auth-ready', 'ready', 'hitl-event'],
  data() {
    return {
      authLoadingSrc,
      /** After embed init, set when the launcher emits micro-bus `APP_READY` (`{ type, content }`). */
      embedAppReady: false,
      phase: 'auth',
      authError: '',
    };
  },
  async mounted() {
    this.ensureScriptsLoaded();

    const tokenOk = await isOrCookieTokenAllowed();
    if (tokenOk) {
      this.phase = 'embed';
      this.$emit('auth-ready');
      await this.$nextTick();
      await this.completeEmbedAfterScripts();
      return;
    }

    const jwt = (this.jwtToken || '').trim();
    if (!jwt) {
      this.authError =
        'No valid or session and no JWT was supplied. Provide jwtToken (or sign in so an or cookie exists).';
      console.error(this.authError);
      return;
    }

    try {
      await exchangeJwtForOrCookie(jwt);
      const allowed = await isOrCookieTokenAllowed();
      if (!allowed) {
        this.authError =
          'The server returned a session, but it was not accepted by the token check. Try a fresh JWT.';
        console.error(this.authError);
        return;
      }
    } catch (e) {
      const msg = e && e.message ? String(e.message) : String(e);
      this.authError = `Authorization failed: ${msg}`;
      console.error('[MicroHitlWidget] JWT exchange failed', e);
      return;
    }

    this.phase = 'embed';
    this.$emit('auth-ready');
    await this.$nextTick();
    await this.completeEmbedAfterScripts();
  },
  beforeUnmount() {
    for (const id of Object.values(SCRIPT_ID)) {
      document.getElementById(id)?.remove();
    }
  },
  methods: {
    /** Single flight: Vue CDN + launcher UMD (safe before auth completes). */
    ensureScriptsLoaded() {
      if (this._scriptLoadPromise) return this._scriptLoadPromise;

      const main = `${HITL_ORIGIN}/core/${VERSION}/${BUNDLE_MAIN}`;
      this._scriptLoadPromise = (async () => {
        await appendScript(VUE_CDN_URL, SCRIPT_ID.vue);
        await appendScript(main, SCRIPT_ID.launcher, { attrs: ['data-onereach-app'] });
      })();

      return this._scriptLoadPromise;
    },

    async completeEmbedAfterScripts() {
      try {
        await this.ensureScriptsLoaded();

        const api = window.HITLAgentUI;
        const init = 
          api && (typeof api.initialize === 'function' ? api.initialize : api.default?.initialize);
        const onEvent = 
          api && (typeof api.onEvent === 'function' ? api.onEvent : api.default?.onEvent);
        const emit = 
          api && (typeof api.emit === 'function' ? api.emit : api.default?.emit);
        const openPopup = 
          api && (typeof api.openPopup === 'function' ? api.openPopup : api.default?.openPopup);
        const closePopup =
          api && (typeof api.closePopup === 'function' ? api.closePopup : api.default?.closePopup);
        const navigate =
          api && (typeof api.navigate === 'function' ? api.navigate : api.default?.navigate);

        const hitl = await init({
          cookieDomain: COOKIE_DOMAIN,
          cookieName: COOKIE_PREFIX,
          useShadow: true,
          position: {
            bottom: '2rem',
            right: '2rem',
          },
          popup: {
            height: '80vh',
            maxHeight: '700px',
            width: '30vw',
            minWidth: '390px',
            maxWidth: '450px',
            borderRadius: '16px',
          },
          button: {
            backgroundColor: '#3aab7e',
            hoverBackgroundColor: '#2f8c67',
            size: '48px',
          },
        });

        // Mini launcher: onEvent(eventName, cb) — payload is `{ type, content }`, not `{ event, data }`.
        onEvent('APP_READY', (payload) => {
          console.log('hitl-event', payload);
          this.embedAppReady = true;
          this.$emit('hitl-event', payload);
        });

        this.$emit('ready', {
          hitl,
          emit,
          openPopup,
          closePopup,
          navigate,
        });
      } catch (e) {
        console.warn('[MicroHitlWidget]', e);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.micro-hitl-widget {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  position: relative;

  &__root {
    flex: 1;
    min-height: 240px;
    position: relative;
  }

  &__auth {
    position: relative;
    flex: 1;
    min-height: 0;
  }

  &__auth-hint {
    margin: 0 0 12px;
    font-size: 14px;
    color: #444;

    &--error {
      color: #c00;
    }
  }

  &__auth-launcher {
    --agent-app-button-size: 48px;
    --agent-app-button-activity-size: 18px;
    position: fixed;
    bottom: 3rem;
    right: 3rem;
    z-index: 2147483000;
    display: flex;
    width: var(--agent-app-button-size);
    pointer-events: none;
  }

  &__auth-app-button {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    width: var(--agent-app-button-size);
    height: var(--agent-app-button-size);
    border-radius: 50%;
    background-color: #2878fd;
    color: #fff;
    cursor: default;
    box-shadow:
      0 3px 5px -1px rgba(0, 0, 0, 0.2),
      0 6px 10px 0 rgba(0, 0, 0, 0.14);
  }

  &__auth-activity {
    flex-shrink: 0;
    width: var(--agent-app-button-activity-size);
    height: var(--agent-app-button-activity-size);
    display: block;
    object-fit: contain;
  }
}
</style>
